import { Skeleton } from "@/components/ui-kit/skeleton";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui-kit/card";
import { CircleCheck, Waypoints, CheckCheck, CircleAlert } from "lucide-react";

import { ElementType } from "react";
import { useTranslation } from 'react-i18next';

type OverviewComponentsProps = {
  title: string;
  value: number;
  className?: string;
  isLoading?: boolean;
  Icon: ElementType;
};

const OverviewComponentsSkelton = () => (
  <Card className="border-none p-0 shadow-none">
    <CardContent className="flex items-center gap-3">
      <Skeleton className="aspect-square w-14" />
      <div className="flex-1">
        <Skeleton className="h-7 w-1/2" />
        <Skeleton className="mt-1 h-5 w-full" />
      </div>
    </CardContent>
  </Card>
);

export const OverviewComponent = ({
  title,
  value,
  className,
  isLoading,
  Icon,
}: OverviewComponentsProps) => {
  if (isLoading) return <OverviewComponentsSkelton />;
  return (
    <Card className="border-none p-0 shadow-none">
      <CardContent className="flex items-center gap-3">
        <div className={cn("rounded-sm bg-blocks-primary-25 dark:bg-primary/20 p-3 text-primary", className)}>
          <Icon className="aspect-square w-8" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const OverviewComponents = ({
  totalCalls,
  successfulCalls,
  failedCalls,
  isLoading,
}: {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <OverviewComponent
        title={t('TOTAL_REQUESTS')}
        Icon={Waypoints}
        value={totalCalls ?? 0}
        isLoading={isLoading}
      />

      <OverviewComponent
        title={t('SUCCESS_RATE')}
        Icon={CircleCheck}
        value={totalCalls ? Math.round((successfulCalls / totalCalls) * 100) : 0}
        className="bg-blocks-secondary-50 dark:bg-cyan-900/20 text-cyan-500 dark:text-cyan-400"
        isLoading={isLoading}
      />

      <OverviewComponent
        title={t('SUCCESS_REQUESTS')}
        Icon={CheckCheck}
        value={successfulCalls ?? 0}
        className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
        isLoading={isLoading}
      />

      <OverviewComponent
        title={t('FAILED_REQUESTS')}
        Icon={CircleAlert}
        value={failedCalls ?? 0}
        className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
        isLoading={isLoading}
      />
    </div>
  );
};
